import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Breadcrumbs,
  Link,
  Switch,
  FormControlLabel,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SyncIcon from '@mui/icons-material/Sync';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import shopifyService from '../../services/shopifyService';
import axios from '../../utils/axios';

const MASKED = '********';

const shopifyLoadError = (err) => {
  const status = err.response?.status;
  const data = err.response?.data;
  const serverMsg =
    (typeof data === 'string' ? data : null) ||
    data?.message ||
    data?.error ||
    err.message;
  if (status === 404) {
    return 'Shopify API not found. Restart the Spring Boot server on the dashboard_UI branch (Shopify code must be compiled and running).';
  }
  if (status === 500 && serverMsg && /shopify_config|sync_products|sync_inventory|Unknown column/i.test(serverMsg)) {
    return 'Database is missing Shopify tables/columns. Restart the backend so Flyway runs migrations V89–V92, then refresh this page.';
  }
  return serverMsg || 'Failed to load Shopify settings';
};

const ShopifySettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const loadErrorShown = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [syncingInventory, setSyncingInventory] = useState(false);
  const [productStats, setProductStats] = useState(null);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    storeUrl: '',
    accessToken: '',
    webhookSecret: '',
    isEnabled: false,
    syncOrders: true,
    syncProducts: true,
    syncInventory: true,
    defaultLocationId: '',
    apiVersion: '2025-01',
    tokenConfigured: false,
    webhookSecretConfigured: false,
  });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      loadErrorShown.current = false;
      await loadAll(cancelled);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const showLoadError = (message, variant = 'error') => {
    if (loadErrorShown.current) return;
    loadErrorShown.current = true;
    enqueueSnackbar(message, { variant, autoHideDuration: variant === 'warning' ? 12000 : 8000 });
  };

  const applyConfigToForm = (config) => {
    setForm({
      storeUrl: config.storeUrl || '',
      accessToken: config.tokenConfigured ? MASKED : '',
      webhookSecret: config.webhookSecretConfigured ? MASKED : '',
      isEnabled: !!config.isEnabled,
      syncOrders: config.syncOrders !== false,
      syncProducts: config.syncProducts !== false,
      syncInventory: config.syncInventory !== false,
      defaultLocationId: config.defaultLocationId || '',
      apiVersion: config.apiVersion || '2025-01',
      tokenConfigured: !!config.tokenConfigured,
      webhookSecretConfigured: !!config.webhookSecretConfigured,
    });
  };

  const loadAll = async (cancelled = false) => {
    try {
      setLoading(true);
      let status;
      try {
        status = await shopifyService.getFeatureStatus();
      } catch (err) {
        if (!cancelled) {
          showLoadError(shopifyLoadError(err));
        }
        return;
      }
      if (cancelled) return;

      if (!status.enabled) {
        showLoadError(
          `Shopify not enabled for company ${status.companyId}. Active Shopify add-on rows: ${status.activeShopifyAddonRows}. Keys: ${(status.activeFeatureKeysFromAddons || []).join(', ') || 'none'}. Re-assign add-on in Superadmin or fix effective_until date.`,
          'warning'
        );
        return;
      }

      let config;
      try {
        config = await shopifyService.getConfig();
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 403) {
            showLoadError(
              'Shopify is not enabled for this company. In Superadmin, assign the Shopify add-on (feature key: shopify_integration) and ensure the add-on is active, then log in again.',
              'warning'
            );
          } else {
            showLoadError(shopifyLoadError(err));
          }
        }
        return;
      }
      if (cancelled) return;
      applyConfigToForm(config);

      try {
        const locRes = await axios.get('/locations', {
          params: { page: 0, size: 200, sort: 'name', direction: 'ASC' },
        });
        const locContent = locRes.data?.content || locRes.data || [];
        setLocations(Array.isArray(locContent) ? locContent : []);
      } catch (locErr) {
        console.warn('Shopify settings: could not load locations', locErr);
        setLocations([]);
      }

      try {
        const mappingsRes = await shopifyService.getProductMappings(0, 1);
        setProductStats({
          successCount: mappingsRes.successCount ?? 0,
          failedCount: mappingsRes.failedCount ?? 0,
          totalElements: mappingsRes.totalElements ?? 0,
        });
      } catch {
        setProductStats(null);
      }
    } catch (err) {
      if (!cancelled) {
        if (err.response?.status === 403) {
          showLoadError(
            'Shopify is not enabled for this company. In Superadmin, assign the Shopify add-on (feature key: shopify_integration) and ensure the add-on is active, then log in again.',
            'warning'
          );
        } else {
          showLoadError(shopifyLoadError(err));
        }
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        storeUrl: form.storeUrl,
        isEnabled: form.isEnabled,
        syncOrders: form.syncOrders,
        syncProducts: form.syncProducts,
        syncInventory: form.syncInventory,
        defaultLocationId: form.defaultLocationId ? Number(form.defaultLocationId) : null,
        apiVersion: form.apiVersion,
      };
      if (form.accessToken && form.accessToken !== MASKED) {
        payload.accessToken = form.accessToken;
      }
      if (form.webhookSecret && form.webhookSecret !== MASKED) {
        payload.webhookSecret = form.webhookSecret;
      }
      await shopifyService.saveConfig(payload);
      enqueueSnackbar('Shopify settings saved', { variant: 'success' });
      await loadAll();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || err.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const res = await shopifyService.testConnection();
      enqueueSnackbar(`Connected to ${res.shopName}`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Connection test failed', { variant: 'error' });
    } finally {
      setTesting(false);
    }
  };

  const handleSyncProducts = async () => {
    try {
      setSyncingProducts(true);
      const res = await shopifyService.syncProducts();
      enqueueSnackbar(res.message || 'Products synced to Shopify', { variant: 'success', autoHideDuration: 8000 });
      await loadAll();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Product sync failed';
      const scopeHint =
        msg.includes('write_products') || msg.includes('merchant approval')
          ? ' Enable write_products (and related) scopes in Shopify, reinstall the app, then update the access token.'
          : '';
      enqueueSnackbar(msg + scopeHint, { variant: 'error', autoHideDuration: 12000 });
    } finally {
      setSyncingProducts(false);
    }
  };

  const handleSyncInventory = async () => {
    try {
      setSyncingInventory(true);
      const res = await shopifyService.syncInventory();
      enqueueSnackbar(res.message || 'Inventory synced to Shopify', { variant: 'success', autoHideDuration: 8000 });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || err.message || 'Inventory sync failed', { variant: 'error' });
    } finally {
      setSyncingInventory(false);
    }
  };

  const handleSyncOrders = async () => {
    try {
      setSyncing(true);
      const res = await shopifyService.syncOrders();
      enqueueSnackbar(`Synced orders (${res.newOrdersCount || 0} new)`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Sync failed', { variant: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/settings/company" underline="hover" color="inherit">
            Settings
          </Link>
          <Typography color="text.primary">Shopify Integration</Typography>
        </Breadcrumbs>

        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <StorefrontIcon /> Shopify Integration
        </Typography>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isEnabled}
                  onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
                />
              }
              label="Enable Shopify sync"
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.syncOrders}
                    onChange={(e) => setForm({ ...form, syncOrders: e.target.checked })}
                  />
                }
                label="Auto sync orders"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.syncProducts}
                    onChange={(e) => setForm({ ...form, syncProducts: e.target.checked })}
                  />
                }
                label="Auto sync products (every 30 min)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.syncInventory}
                    onChange={(e) => setForm({ ...form, syncInventory: e.target.checked })}
                  />
                }
                label="Auto sync inventory (every 15 min)"
              />
            </Box>
            {productStats && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Product mappings: {productStats.successCount} synced, {productStats.failedCount} failed (
                {productStats.totalElements} total)
              </Typography>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Store URL"
                  placeholder="mystore.myshopify.com"
                  value={form.storeUrl}
                  onChange={(e) => setForm({ ...form, storeUrl: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Admin API access token"
                  type="password"
                  value={form.accessToken}
                  onChange={(e) => setForm({ ...form, accessToken: e.target.value })}
                  helperText={form.tokenConfigured ? 'Leave blank to keep existing token' : 'From Shopify custom app'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Webhook secret"
                  type="password"
                  value={form.webhookSecret}
                  onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })}
                  helperText="Used to verify Shopify webhooks"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Default location (stock)</InputLabel>
                  <Select
                    value={form.defaultLocationId}
                    label="Default location (stock)"
                    onChange={(e) => setForm({ ...form, defaultLocationId: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>Select location</em>
                    </MenuItem>
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="API version"
                  value={form.apiVersion}
                  onChange={(e) => setForm({ ...form, apiVersion: e.target.value })}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={handleTest} disabled={testing}>
                {testing ? 'Testing…' : 'Test connection'}
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSyncProducts} disabled={syncingProducts}>
                {syncingProducts ? 'Syncing products…' : 'Sync products to Shopify'}
              </Button>
              <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSyncInventory} disabled={syncingInventory}>
                {syncingInventory ? 'Syncing stock…' : 'Sync inventory to Shopify'}
              </Button>
              <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSyncOrders} disabled={syncing}>
                {syncing ? 'Syncing…' : 'Sync orders now'}
              </Button>
              <Button component={RouterLink} to="/shopify/orders" variant="text">
                View Shopify orders
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
};

export default ShopifySettings;
